import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

interface WaitingRoomProps {
  status: 'searching' | 'found' | 'waiting';
  opponentName?: string;
  onReady?: () => void;
  onCancel: () => void;
  isReady?: boolean;
  opponentReady?: boolean;
}

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: ${({ theme }) => theme.spacing.lg};
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const Title = styled(motion.h1)`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin: 0;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0;
  max-width: 500px;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${({ theme }) => theme.colors.surface};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const SearchingDots = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  justify-content: center;
`;

const Dot = styled.span<{ $delay: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  animation: ${pulse} 1.4s ease-in-out ${({ $delay }) => $delay}s infinite;
`;

const OpponentCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 300px;

  ${({ theme }) => theme.breakpoints.mobile} {
    min-width: 250px;
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
`;

const Avatar = styled.div<{ $avatar: number }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  border: 4px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const OpponentName = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  text-align: center;

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1.25rem;
  }
`;

const ReadyBadge = styled(motion.div)<{ $isReady: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme, $isReady }) =>
    $isReady ? theme.colors.success : theme.colors.warning};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
`;

const PlayersContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin: ${({ theme }) => theme.spacing.xl} 0;
`;

const VersusText = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1.5rem;
  }
`;

const Button = styled(motion.button)<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;

  background: ${({ theme, $variant }) => {
    if ($variant === 'danger') return theme.colors.error;
    if ($variant === 'secondary') return theme.colors.surface;
    return theme.colors.primary;
  }};

  color: ${({ theme, $variant }) =>
    $variant === 'secondary' ? theme.colors.text : '#fff'};

  box-shadow: ${({ theme }) => theme.shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 1rem;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    min-width: 150px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
`;

const getAvatarEmoji = (avatar: number): string => {
  const avatars = ['😇', '🙏', '✝️', '📖', '⛪', '🕊️', '🌟', '💫', '👼', '🔔'];
  return avatars[avatar] || '😊';
};

const WaitingRoom: React.FC<WaitingRoomProps> = ({
  status,
  opponentName,
  onReady,
  onCancel,
  isReady = false,
  opponentReady = false,
}) => {
  console.log('🎭 WaitingRoom render - status:', status, 'isReady:', isReady, 'opponentReady:', opponentReady);

  // Auto-ready after seeing opponent
  useEffect(() => {
    if (status === 'waiting' && onReady && !isReady && opponentName) {
      // Auto-mark ready after 2.5 seconds (time to see opponent and build anticipation)
      const timer = setTimeout(() => {
        console.log('⏰ Auto-marking ready...');
        onReady();
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [status, onReady, isReady, opponentName]);

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      <AnimatePresence mode="wait">
        {status === 'searching' && (
          <motion.div
            key="searching"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem' }}
          >
            <LoadingSpinner />
            <Title>Searching for Opponent</Title>
            <Subtitle>
              Finding someone to test your Bible knowledge against...
            </Subtitle>
            <SearchingDots>
              <Dot $delay={0} />
              <Dot $delay={0.2} />
              <Dot $delay={0.4} />
            </SearchingDots>
            <Button $variant="danger" onClick={onCancel} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              Cancel Search
            </Button>
          </motion.div>
        )}

        {status === 'found' && (
          <motion.div
            key="found"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem' }}
          >
            <Title>Opponent Found! 🎉</Title>
            <Subtitle>Preparing the match...</Subtitle>
            <LoadingSpinner />
          </motion.div>
        )}

        {status === 'waiting' && opponentName && (
          <motion.div
            key="waiting"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem', width: '100%' }}
          >
            <Title>Match Starting Soon!</Title>

            <PlayersContainer>
              <OpponentCard
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <AvatarContainer>
                  <Avatar $avatar={0}>😇</Avatar>
                </AvatarContainer>
                <OpponentName>You</OpponentName>
                <ReadyBadge
                  $isReady={isReady}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {isReady ? '✓ Ready!' : 'Not Ready'}
                </ReadyBadge>
              </OpponentCard>

              <VersusText>VS</VersusText>

              <OpponentCard
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <AvatarContainer>
                  <Avatar $avatar={1}>🙏</Avatar>
                </AvatarContainer>
                <OpponentName>{opponentName}</OpponentName>
                <ReadyBadge
                  $isReady={opponentReady}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {opponentReady ? '✓ Ready!' : 'Not Ready'}
                </ReadyBadge>
              </OpponentCard>
            </PlayersContainer>

            {isReady && opponentReady ? (
              <Subtitle>
                <strong>Both players ready! Starting match...</strong>
              </Subtitle>
            ) : (
              <Subtitle>
                Waiting for both players to be ready...
              </Subtitle>
            )}

            <ButtonGroup>
              {!isReady && onReady && (
                <Button onClick={onReady} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  I'm Ready!
                </Button>
              )}
              <Button
                $variant="secondary"
                onClick={onCancel}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Leave Match
              </Button>
            </ButtonGroup>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default WaitingRoom;
